import { useEffect } from 'react';
import useSWR, { type SWRConfiguration } from 'swr';
import denApi from '@/lib/api/denApi';
import { useAuthClient } from '@/lib/authClient';
import { setOrgCookie } from '@/lib/api/orgCookie';
import type { Organization } from '@/types/organization';

/**
 * `null` is an expected waiting state: the authenticated user exists, but an
 * admin has not yet approved their organization membership.
 */
type OrganizationFetchResult = Organization | null;

type UseMyOrganizationOptions = {
  /**
   * When true, requests `addressHealth` from the API
   */
  includeAddressHealth?: boolean;
  /**
   * Optional external fetch gate.
   * Callers can pause org fetches (e.g. until mobile connection status is known)
   * without coupling this hook to other hooks.
   */
  enabled?: boolean;
  /**
   * Optional SWR polling interval for membership changes. The expected
   * pre-approval state is returned by the API as `200 null`, so SWR can keep
   * polling and apply its visibility and connectivity checks between requests.
   */
  refreshInterval?: SWRConfiguration<OrganizationFetchResult>['refreshInterval'];
};

type OrganizationFetcherKey = {
  url: string;
  includeAddressHealth: boolean;
};

/**
 * Fetches the current user's organization with optional address health.
 *
 * `GET /organization/my` returns `200 null` while a connected user is waiting
 * for admin approval. That expected result remains normal SWR data, allowing
 * `refreshInterval` to continue without suppressing genuine request errors.
 */
const fetchOrganization = async ({
  url,
  includeAddressHealth,
}: OrganizationFetcherKey): Promise<OrganizationFetchResult> =>
  denApi.get<OrganizationFetchResult, { includeAddressHealth: boolean }>(url, {
    includeAddressHealth,
  });

// Organization with computed auth context
type MyOrganization = Organization & {
  isAdmin: boolean;
};

/**
 * Hook that returns organization data with isAdmin computed.
 * Fetches only when the user has a Better Auth session *and* a linked mobile session
 * (`GET /organization/my` uses mobileConnectedMiddleware — same gate as `useDeviceConnection`).
 *
 * Callers may provide `refreshInterval` to poll a pending membership. The
 * fetcher represents that pending state as `null`, while this hook continues to
 * expose `organization` as `undefined` until approval succeeds.
 *
 * Also sets the x-current-org-id cookie for backend authentication.
 */
export function useMyOrganization(options?: UseMyOrganizationOptions): {
  organization?: MyOrganization;
  addressHealth?: Organization['addressHealth'];
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
} {
  const includeAddressHealth = options?.includeAddressHealth === true;
  const enabled = options?.enabled !== false;
  const { session, user } = useAuthClient();

  const swrKey: OrganizationFetcherKey | null =
    session.data && enabled
      ? { url: 'organization/my', includeAddressHealth }
      : null;

  const {
    data,
    error,
    isLoading: isOrganizationFetchLoading,
    mutate,
  } = useSWR<OrganizationFetchResult>(swrKey, fetchOrganization, {
    revalidateOnFocus: false,
    ...(options?.refreshInterval !== undefined && {
      refreshInterval: options.refreshInterval,
    }),
  });

  const isLoading =
    session.isPending || (swrKey !== null && isOrganizationFetchLoading);

  // Set the organization cookie when organization data is loaded
  // This cookie is required by the backend for org-level authentication
  useEffect(() => {
    if (data?.id) {
      setOrgCookie(data.id);
    } else if (!session.data) {
      // Clear cookie when user is logged out
      setOrgCookie(null);
    }
  }, [data?.id, session.data]);

  // Compute isAdmin based on organization adminUsers and current user
  const isAdmin = data && user ? data.adminUsers.includes(user.id) : false;

  const organization = data
    ? {
        id: data.id,
        name: data.name,
        adminUsers: data.adminUsers,
        numberOfApprovalsNeeded: data.numberOfApprovalsNeeded,
        expirationMinutes: data.expirationMinutes,
        hideTransactionsFromNonPolicyParticipants:
          data.hideTransactionsFromNonPolicyParticipants,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        ...(data.addressHealth !== undefined && {
          addressHealth: data.addressHealth,
        }),
        isAdmin,
      }
    : undefined;

  return {
    ...(organization && { organization }),
    addressHealth: data?.addressHealth,
    isLoading,
    error,
    refetch: mutate,
  };
}
